import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { CronJob, CronJobType, CronJobValues } from "../../service/CronJobService";
import { cronPresets, describeCron } from "../../utils/describeCron";

interface CronJobDialogProps {
  open: boolean;
  cronJob: CronJob | null; // null = create
  jobTypes: CronJobType[];
  onClose: () => void;
  onSubmit: (values: CronJobValues) => Promise<boolean>;
}

const emptyValues: CronJobValues = {
  name: "",
  jobType: "",
  schedule: "",
  timezone: "Europe/Copenhagen",
  enabled: true,
  description: "",
};

const CronJobDialog: React.FC<CronJobDialogProps> = ({ open, cronJob, jobTypes, onClose, onSubmit }) => {
  const [t] = useTranslation("global");

  const validationSchema = Yup.object({
    name: Yup.string().trim().required(t("cronJobs.form.nameRequired")),
    jobType: Yup.string().required(t("cronJobs.form.jobTypeRequired")),
    // Only a shape check - the backend validates the expression properly
    schedule: Yup.string()
      .trim()
      .required(t("cronJobs.form.scheduleRequired"))
      .matches(/^(\S+\s+){4}\S+$/, t("cronJobs.form.scheduleFormat")),
    timezone: Yup.string().trim().required(t("cronJobs.form.timezoneRequired")),
  });

  const formik = useFormik<CronJobValues>({
    initialValues: emptyValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const saved = await onSubmit(values);
      setSubmitting(false);
      if (saved) onClose();
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm({
        values: cronJob
          ? {
              name: cronJob.name,
              jobType: cronJob.jobType,
              schedule: cronJob.schedule,
              timezone: cronJob.timezone,
              enabled: cronJob.enabled,
              description: cronJob.description || "",
            }
          : emptyValues,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cronJob]);

  const handleJobTypeChange = (key: string) => {
    formik.setFieldValue("jobType", key);
    const jobType = jobTypes.find((type) => type.key === key);
    // Fill in the job type's defaults when creating, so the admin only has to adjust them
    if (!cronJob && jobType) {
      if (!formik.values.name) formik.setFieldValue("name", jobType.label);
      if (!formik.values.schedule) formik.setFieldValue("schedule", jobType.defaultSchedule);
      if (!formik.values.description) formik.setFieldValue("description", jobType.description);
    }
  };

  const scheduleText = describeCron(formik.values.schedule, t);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{cronJob ? t("cronJobs.form.editTitle") : t("cronJobs.form.createTitle")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: "8px !important" }}>
          <TextField
            select
            label={t("cronJobs.form.jobType")}
            name="jobType"
            value={formik.values.jobType}
            onChange={(e) => handleJobTypeChange(e.target.value)}
            error={formik.touched.jobType && Boolean(formik.errors.jobType)}
            helperText={
              (formik.touched.jobType && formik.errors.jobType) ||
              jobTypes.find((type) => type.key === formik.values.jobType)?.description
            }
          >
            {jobTypes.map((type) => (
              <MenuItem key={type.key} value={type.key}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("cronJobs.form.name")}
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <Box>
            <TextField
              fullWidth
              label={t("cronJobs.form.schedule")}
              name="schedule"
              placeholder="0 8 * * *"
              value={formik.values.schedule}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.schedule && Boolean(formik.errors.schedule)}
              helperText={
                (formik.touched.schedule && formik.errors.schedule) || scheduleText || t("cronJobs.form.scheduleHelp")
              }
              inputProps={{ style: { fontFamily: "monospace" } }}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, marginTop: 1 }}>
              {cronPresets.map((preset) => (
                <Chip
                  key={preset.key}
                  label={t(`cronJobs.presets.${preset.key}`)}
                  onClick={() => formik.setFieldValue("schedule", preset.schedule)}
                  color={formik.values.schedule === preset.schedule ? "primary" : "default"}
                  size="small"
                />
              ))}
            </Box>
          </Box>
          <TextField
            label={t("cronJobs.form.timezone")}
            name="timezone"
            value={formik.values.timezone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.timezone && Boolean(formik.errors.timezone)}
            helperText={formik.touched.timezone && formik.errors.timezone}
          />
          <TextField
            label={t("cronJobs.form.description")}
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.enabled}
                onChange={(e) => formik.setFieldValue("enabled", e.target.checked)}
              />
            }
            label={t("cronJobs.form.enabled")}
          />
          <Typography variant="caption" color="text.secondary">
            {t("cronJobs.form.syntaxHint")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("cronJobs.cancel")}</Button>
          <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
            {cronJob ? t("cronJobs.save") : t("cronJobs.create")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CronJobDialog;
